<script>
import { ArrowUpIcon } from "vue-feather-icons";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";

/**
 * V4 early-access landing page — what's new in Design Mirror V4,
 * downloads (desktop installers + mobile apps) and install steps.
 */
export default {
  data() {
    return {
      // Current early-access build shown on the page.
      version: "4.0.0",
      // Download links per platform (keys match beta.download.items in locale).
      // Desktop installers + plugin pack live in oss://devproducts/design-mirror-app/
      // (CDN: product.cutterman.cn); Android goes through pgyer.
      downloads: {
        windows: "https://product.cutterman.cn/design-mirror-app/DesignMirror-Setup-4.0.0.exe",
        mac: "https://product.cutterman.cn/design-mirror-app/DesignMirror-4.0.0.pkg",
        android: "https://www.pgyer.com/designmirror",
        ios: null, // App Store build still in review — rendered as "coming soon"
        plugins: "https://product.cutterman.cn/design-mirror-app/DesignMirror-Plugins-4.0.0.zip",
      },
      // Feedback WeChat QR. Swap this file to update the code shown on the page.
      qrImage: "images/wechat-mirror-test.jpg",
      // uil icon per feature card (parallel to beta.features.list)
      featureIcons: [
        "uil-wifi-router",
        "uil-plug",
        "uil-mobile-android",
        "uil-layers",
        "uil-bolt-alt",
        "uil-mobile-vibrate",
      ],
    };
  },
  methods: {
    track(key) {
      if (window._czc) {
        window._czc.push(["_trackEvent", "app", "download", "v4-" + key]);
      }
    },
  },
  components: {
    Navbar,
    Footer,
    ArrowUpIcon,
  },
};
</script>

<template>
  <div>
    <Navbar />

    <!-- Hero Start -->
    <section class="bg-half-170 d-table w-100" id="home"
             style="background: linear-gradient(90deg, rgba(47,85,212,0.06), rgba(47,85,212,0.0));">
      <div class="container">
        <div class="row mt-5 align-items-center">
          <div class="col-lg-7 col-md-7">
            <div class="title-heading">
              <span class="badge badge-pill badge-soft-primary mb-3">{{ $t('beta.hero.eyebrow') }}</span>
              <h1 class="heading mb-3" v-html="$t('beta.hero.title')"></h1>
              <p class="para-desc text-muted" v-html="$t('beta.hero.description')"></p>
              <div class="mt-4">
                <a href="javascript:void(0)" v-scroll-to="'#download'" class="btn btn-primary mt-2 mr-2">
                  <i class="uil uil-file-download"></i> {{ $t('beta.hero.cta') }}
                </a>
              </div>
            </div>
          </div>
          <!--end col-->

          <div class="col-lg-5 col-md-5 mt-4 pt-2 mt-sm-0 pt-sm-0">
            <div class="text-center">
              <video
                src="images/v4-demo.mp4"
                class="img-fluid rounded shadow"
                style="max-height: 560px;"
                autoplay
                loop
                muted
                playsinline
                preload="metadata"
              ></video>
            </div>
          </div>
          <!--end col-->
        </div>
        <!--end row-->
      </div>
      <!--end container-->
    </section>
    <!--end section-->
    <!-- Hero End -->

    <!-- Shape Start -->
    <div class="position-relative">
      <div class="shape overflow-hidden text-light">
        <svg viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor"></path>
        </svg>
      </div>
    </div>
    <!--Shape End-->

    <!-- Features Start -->
    <section class="section bg-light">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-12 text-center">
            <div class="section-title mb-4 pb-2">
              <h4 class="title mb-4">{{ $t('beta.features.title') }}</h4>
              <p class="text-muted para-desc mb-0 mx-auto">{{ $t('beta.features.description') }}</p>
            </div>
          </div>
          <!--end col-->
        </div>
        <!--end row-->

        <div class="row">
          <div class="col-lg-4 col-md-6 col-12 mt-4 pt-2"
               v-for="(item, index) in $t('beta.features.list')" :key="index">
            <div class="card features feature-primary border-0 rounded shadow p-4 h-100 bg-white">
              <div class="icon text-center rounded-circle text-primary mr-3 mb-3">
                <i :class="['uil', featureIcons[index]]" style="font-size: 30px;"></i>
              </div>
              <div class="content">
                <h5 class="mb-2">{{ item.title }}</h5>
                <p class="text-muted mb-0" v-html="item.description"></p>
              </div>
            </div>
          </div>
          <!--end col-->
        </div>
        <!--end row-->
      </div>
      <!--end container-->
    </section>
    <!--end section-->
    <!-- Features End -->

    <!-- Download Start -->
    <section class="section" id="download">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-12 text-center">
            <div class="section-title mb-4 pb-2">
              <h4 class="title mb-4"><i class="uil uil-file-download text-primary"></i> {{ $t('beta.download.title') }}</h4>
              <p class="text-muted para-desc mb-2 mx-auto">{{ $t('beta.download.description') }}</p>
              <p class="text-muted small mb-0">{{ $t('beta.download.desktop_hint', { version: version }) }}</p>
            </div>
          </div>
        </div>
        <!--end row-->

        <div class="row">
          <div class="col-lg-3 col-md-6 col-12 mt-4 pt-2"
               v-for="(item, index) in $t('beta.download.items')" :key="index">
            <div class="card explore-feature border-0 rounded text-center bg-white shadow p-4 h-100 d-flex flex-column">
              <div class="icon rounded-circle shadow-lg d-inline-block mx-auto">
                <i class="uil text-primary" :class="item.icon" style="font-size: 28px;"></i>
              </div>
              <div class="mt-3 flex-grow-1">
                <h5>{{ item.title }}</h5>
                <p class="text-muted">{{ item.description }}</p>
              </div>
              <div class="mt-2">
                <a v-if="downloads[item.key]" :href="downloads[item.key]" target="_blank" rel="noopener"
                   class="btn btn-primary btn-block" @click="track(item.key)">
                  <i class="uil uil-import"></i> {{ item.button }}
                </a>
                <a v-else href="javascript:void(0)" class="btn btn-secondary btn-block disabled">
                  <i class="uil uil-hourglass"></i> {{ item.button }}
                </a>
              </div>
            </div>
          </div>
          <!--end col-->
        </div>
        <!--end row-->

        <!-- Plugin pack (Figma / MasterGo / JSDesign) -->
        <div class="row justify-content-center mt-4 pt-2">
          <div class="col-lg-10">
            <div class="card border-0 rounded shadow bg-white p-4">
              <div class="d-flex flex-column flex-md-row align-items-md-center">
                <i class="uil uil-puzzle-piece text-primary mr-md-4 mb-3 mb-md-0" style="font-size: 40px;"></i>
                <div class="media-body mr-md-4">
                  <h5 class="mb-1">{{ $t('beta.download.plugins.title') }}</h5>
                  <p class="text-muted mb-0">{{ $t('beta.download.plugins.description') }}</p>
                </div>
                <div class="mt-3 mt-md-0">
                  <a :href="downloads.plugins" target="_blank" rel="noopener"
                     class="btn btn-primary text-nowrap" @click="track('plugins')">
                    <i class="uil uil-import"></i> {{ $t('beta.download.plugins.button') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!--end row-->
      </div>
      <!--end container-->
    </section>
    <!-- Download End -->

    <!-- Install + Contact Start -->
    <section class="section bg-light" id="install">
      <div class="container">
        <div class="row align-items-center">
          <!-- Steps -->
          <div class="col-lg-7 col-md-7">
            <div class="section-title">
              <h4 class="title mb-2">{{ $t('beta.install.title') }}</h4>
              <p class="text-muted mb-4">{{ $t('beta.install.description') }}</p>
            </div>
            <div class="media key-feature align-items-center p-3 rounded shadow-sm bg-white mb-3"
                 v-for="(step, index) in $t('beta.install.steps')" :key="index">
              <div class="icon text-center rounded-circle bg-soft-primary text-primary mr-3 d-flex align-items-center justify-content-center"
                   style="width: 44px; height: 44px; min-width: 44px; font-weight: 600; font-size: 18px;">
                {{ index + 1 }}
              </div>
              <div class="media-body">
                <h5 class="mb-1">{{ step.title }}</h5>
                <p class="text-muted mb-0">{{ step.description }}</p>
              </div>
            </div>

            <!-- Figma / MasterGo / JSDesign plugin-pack import guide -->
            <div class="card border-0 rounded shadow-sm bg-white p-3 mt-4">
              <h5 class="mb-2"><i class="uil uil-puzzle-piece text-primary mr-1"></i>{{ $t('beta.install.web_plugins.title') }}</h5>
              <p class="text-muted mb-2">{{ $t('beta.install.web_plugins.description') }}</p>
              <ul class="list-unstyled mb-2">
                <li class="text-muted mb-2" v-for="(it, i) in $t('beta.install.web_plugins.items')" :key="i">
                  <i class="uil uil-check-circle text-primary mr-2"></i>{{ it }}
                </li>
              </ul>
              <p class="text-muted small mb-0">
                <i class="uil uil-info-circle text-primary mr-1"></i>{{ $t('beta.install.web_plugins.note') }}
              </p>
            </div>
          </div>
          <!--end col-->

          <!-- Feedback QR card -->
          <div class="col-lg-5 col-md-5 mt-4 mt-sm-0 pt-2 pt-sm-0">
            <div class="card rounded shadow border-0 text-center p-4 bg-white">
              <h5 class="mb-3">{{ $t('beta.contact.title') }}</h5>
              <div class="text-center">
                <img :src="qrImage" alt="WeChat QR"
                     class="img-fluid rounded border p-2 bg-white"
                     style="max-width: 240px;" />
              </div>
              <p class="text-primary font-weight-bold mt-3 mb-1">
                <i class="uil uil-qrcode-scan mr-1"></i>{{ $t('beta.contact.qrhint') }}
              </p>
              <p class="text-muted small mb-0">{{ $t('beta.contact.description') }}</p>
              <p class="text-muted small mt-2 mb-0">{{ $t('beta.contact.fallback') }}</p>
            </div>
          </div>
          <!--end col-->
        </div>
        <!--end row-->
      </div>
      <!--end container-->
    </section>
    <!-- Install + Contact End -->

    <Footer />

    <!-- Back to top -->
    <a href="javascript: void(0);" class="btn btn-icon btn-primary back-to-top"
       id="back-to-top" v-scroll-to="'#topnav'">
      <arrow-up-icon class="icons"></arrow-up-icon>
    </a>
    <!-- Back to top -->
  </div>
</template>
